/**
 * CreativeCard component for displaying creative thumbnails with metadata
 * Shows decision badge, duration, and analysis status
 * Includes delete action with confirmation modal
 */

import { useQueryClient } from '@tanstack/react-query';
import { Play, Clock, TrendingUp, AlertTriangle, XCircle, Trash2, MoreVertical, Copy, Image } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { CopyCreativeModal } from '@/components/creative/CopyCreativeModal';
import { Card } from '@/components/ui/Card';
import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { useToast } from '@/components/ui/Toast';
import { analysesKeys } from '@/hooks/useAnalysis';
import { useDeleteCreative, useCopyCreative } from '@/hooks/useCreatives';
import { cn } from '@/lib/utils';
import { api } from '@/services/api';
import type { Creative, Analysis, AnalysisDecision } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface CreativeCardProps {
  creative: Creative;
  showCheckbox?: boolean;
  isSelected?: boolean;
  isCompareDisabled?: boolean;
  onSelect?: (creativeId: string, selected: boolean) => void;
  showActions?: boolean;
  onDeleted?: () => void;
  onCopied?: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatSyncTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) {
    return 'just now';
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Get primary placement from unified placements array or legacy fields
 * Returns the first placement (highest impressions) or falls back to legacy
 */
function getPrimaryPlacement(creative: Creative): string | undefined {
  // New unified field takes precedence
  if (creative.placements && creative.placements.length > 0) {
    return creative.placements[0]!.placement;
  }
  // Fall back to legacy fields
  if (creative.metaPlacement) {
    return creative.metaPlacement;
  }
  if (creative.metadata?.placement) {
    return creative.metadata.placement;
  }
  return undefined;
}

/**
 * Check if a creative is an image based on mediaType or format
 */
function isImageCreative(creative: Creative): boolean {
  if (creative.mediaType === 'image') return true;
  const imageFormats = ['jpg', 'jpeg', 'png', 'webp'];
  return imageFormats.includes(creative.format?.toLowerCase() || '');
}

/**
 * Format placements for card view: "Feed +2" or just "Feed"
 */
function formatPlacementsCompact(creative: Creative): string | undefined {
  const primary = getPrimaryPlacement(creative);
  if (!primary) return undefined;

  const totalCount = creative.placements?.length || 1;
  const additional = totalCount - 1;

  return additional > 0 ? `${primary} +${additional}` : primary;
}

// ============================================================================
// META LOGO BADGE COMPONENT
// ============================================================================

/**
 * Meta (Facebook) logo icon for imported creatives
 * Shows as a small badge in top-right corner of thumbnail
 */
function MetaBadge() {
  return (
    <div
      className="absolute top-2 right-2 w-6 h-6 rounded-md bg-white/90 flex items-center justify-center shadow-sm"
      title="Imported from Meta Ads"
    >
      <svg
        viewBox="0 0 24 24"
        className="w-4 h-4"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Meta infinity logo */}
        <path
          d="M12 10.5c-1.5-2.1-3-3.5-4.5-3.5C5.5 7 4 9 4 12s1.5 5 3.5 5c1.5 0 3-1.4 4.5-3.5 1.5 2.1 3 3.5 4.5 3.5 2 0 3.5-2 3.5-5s-1.5-5-3.5-5c-1.5 0-3 1.4-4.5 3.5z"
          stroke="#0081FB"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// ============================================================================
// DECISION BADGE COMPONENT
// ============================================================================

interface DecisionBadgeProps {
  decision: AnalysisDecision;
}

function DecisionBadge({ decision }: DecisionBadgeProps) {
  const config = {
    Scale: {
      bg: 'bg-success-soft',
      text: 'text-success',
      icon: TrendingUp,
    },
    Fix: {
      bg: 'bg-warning-soft',
      text: 'text-warning',
      icon: AlertTriangle,
    },
    Kill: {
      bg: 'bg-danger-soft',
      text: 'text-danger',
      icon: XCircle,
    },
  };

  const { bg, text, icon: Icon } = config[decision];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide',
        bg,
        text
      )}
    >
      <Icon className="h-3 w-3" />
      {decision}
    </div>
  );
}

// ============================================================================
// SKELETON COMPONENT
// ============================================================================

export function CreativeCardSkeleton() {
  return (
    <Card className="overflow-hidden p-0">
      <div className="aspect-video bg-surface-subtle animate-pulse" />
      <div className="p-4 space-y-3">
        <div className="h-4 w-3/4 bg-surface-subtle rounded animate-pulse" />
        <div className="flex items-center justify-between">
          <div className="h-3 w-16 bg-surface-subtle rounded animate-pulse" />
          <div className="h-5 w-14 bg-surface-subtle rounded animate-pulse" />
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// ACTION MENU COMPONENT
// ============================================================================

interface ActionMenuProps {
  onDelete: () => void;
  onCopy: () => void;
}

function ActionMenu({ onDelete, onCopy }: ActionMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={cn(
          'p-1.5 rounded-md transition-colors',
          'text-muted hover:text-primary hover:bg-surface-subtle',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50'
        )}
        aria-label="More actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-10 w-44 bg-surface rounded-lg shadow-lg border border-border-subtle py-1">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              onCopy();
            }}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
              'text-primary hover:bg-surface-subtle transition-colors'
            )}
          >
            <Copy className="h-4 w-4" />
            Copy to Project
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              onDelete();
            }}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
              'text-danger hover:bg-danger-soft transition-colors'
            )}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CREATIVE CARD COMPONENT
// ============================================================================

export function CreativeCard({
  creative,
  showCheckbox = false,
  isSelected = false,
  isCompareDisabled = false,
  onSelect,
  showActions = true,
  onDeleted,
  onCopied,
}: CreativeCardProps) {
  // Debug: Log creative data including thumbnailUrl
  React.useEffect(() => {
    console.log('[CreativeCard] Rendering creative:', {
      creativeId: creative.creativeId,
      name: creative.name,
      thumbnailUrl: creative.thumbnailUrl,
      hasThumbnail: !!creative.thumbnailUrl,
      metaAdId: creative.metaAdId,
      isMetaImport: !!creative.metaAdId,
    });
  }, [creative.creativeId, creative.name, creative.thumbnailUrl, creative.metaAdId]);

  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showCopyModal, setShowCopyModal] = React.useState(false);
  const deleteCreativeMutation = useDeleteCreative(creative.creativeId);
  const copyCreativeMutation = useCopyCreative(creative.creativeId);
  const toast = useToast();

  const handlePrefetchAnalyses = React.useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: analysesKeys.list({ creativeId: creative.creativeId }),
      queryFn: () => api.get<Analysis[]>(`/creatives/${creative.creativeId}/analyses`),
      staleTime: 2 * 60 * 1000,
    });
  }, [queryClient, creative.creativeId]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect?.(creative.creativeId, e.target.checked);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (showCheckbox) {
      e.preventDefault();
      onSelect?.(creative.creativeId, !isSelected);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleCopy = () => {
    setShowCopyModal(true);
  };

  const handleConfirmCopy = async (targetProjectId: string) => {
    try {
      await copyCreativeMutation.mutateAsync({ targetProjectId });
      setShowCopyModal(false);
      toast.success('Creative copied', 'The creative has been copied to the target project');
      onCopied?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Please try again';
      toast.error('Failed to copy creative', message);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCreativeMutation.mutateAsync();
      setShowDeleteModal(false);
      onDeleted?.();
    } catch {
      // Error already logged by onError handler, close modal anyway
      setShowDeleteModal(false);
    }
  };

  const cardContent = (
    <Card
      className={cn(
        'overflow-hidden p-0 transition-all cursor-pointer group',
        'hover:border-border-strong hover:shadow-md',
        showCheckbox && isSelected && 'ring-2 ring-brand border-brand',
        isCompareDisabled && 'opacity-50 cursor-not-allowed'
      )}
      onClick={showCheckbox ? handleCardClick : undefined}
      title={isCompareDisabled ? 'Only creatives of the same media type can be compared' : undefined}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-surface-subtle overflow-hidden">
        {creative.thumbnailUrl ? (
          <img
            src={creative.thumbnailUrl}
            alt={creative.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-surface-subtle to-border-subtle">
            {isImageCreative(creative) ? (
              <Image className="h-12 w-12 text-muted" />
            ) : (
              <Play className="h-12 w-12 text-muted" />
            )}
          </div>
        )}

        {/* Duration / IMG badge */}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 rounded text-xs font-medium">
          {isImageCreative(creative) ? (
            <span className="inline-flex items-center gap-1">
              <Image className="h-3 w-3" />
              IMG
            </span>
          ) : (
            formatDuration(creative.duration)
          )}
        </div>

        {/* Meta badge for imported creatives */}
        {creative.metaAdId && <MetaBadge />}

        {/* Checkbox overlay */}
        {showCheckbox && (
          <div className="absolute top-2 left-2">
            <label
              className="flex items-center justify-center w-6 h-6 rounded-md bg-surface border border-border-subtle cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxChange}
                className="sr-only"
              />
              {isSelected && (
                <svg
                  className="h-4 w-4 text-brand"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </label>
          </div>
        )}

        {/* Play/View button overlay on hover */}
        {!showCheckbox && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
              {isImageCreative(creative) ? (
                <Image className="h-5 w-5 text-primary" />
              ) : (
                <Play className="h-5 w-5 text-primary ml-0.5" fill="currentColor" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Title row with action menu */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-primary line-clamp-1 group-hover:text-brand transition-colors flex-1">
            {creative.name}
          </h3>
          {showActions && !showCheckbox && (
            <ActionMenu onDelete={handleDelete} onCopy={handleCopy} />
          )}
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted">
            <Clock className="h-3 w-3" />
            <span>{formatDate(creative.createdAt)}</span>
          </div>

          {/* Decision badge or status */}
          {creative.latestDecision ? (
            <DecisionBadge decision={creative.latestDecision} />
          ) : creative.status === 'processing' ? (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-info-soft text-info">
              <div className="h-2 w-2 rounded-full bg-info animate-pulse" />
              Analyzing
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-surface-subtle text-muted">
              Not analyzed
            </div>
          )}
        </div>

        {/* Metadata tags */}
        <div className="flex flex-wrap gap-1.5">
          {creative.metadata?.objective && (
            <span className="px-2 py-0.5 rounded text-xs bg-surface-subtle text-secondary">
              {creative.metadata.objective}
            </span>
          )}
          {/* Unified placements display */}
          {formatPlacementsCompact(creative) && (
            <span className="px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700">
              {formatPlacementsCompact(creative)}
            </span>
          )}
        </div>

        {/* Metrics sync timestamp for Meta-imported creatives */}
        {creative.metaAdId && creative.metricsLastSynced && (
          <div className="text-xs text-muted flex items-center gap-1">
            <span>Metrics synced {formatSyncTime(creative.metricsLastSynced)}</span>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <>
      {showCheckbox ? (
        cardContent
      ) : (
        <Link to={`/creatives/${creative.creativeId}`} onMouseEnter={handlePrefetchAnalyses}>
          {cardContent}
        </Link>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Creative"
        description="This action cannot be undone. The creative and all associated analysis data will be permanently deleted."
        itemName={creative.name}
        confirmText="Delete Creative"
        isLoading={deleteCreativeMutation.isPending}
      />

      <CopyCreativeModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        onCopy={handleConfirmCopy}
        creative={creative}
        isLoading={copyCreativeMutation.isPending}
      />
    </>
  );
}
